import React, { useState } from "react";
import { bool, number, object, oneOfType, shape, string } from "prop-types";
import { alpha } from "@mui/material/styles";
import { Box, Stack, Typography } from "@mui/material";

import VideoCard from "./VideoCard";
import ItemBadges from "./ItemBadges";
import { formateDate } from "../utils/date";
import VideoDialog from "./VideoDialog";
import { ClippedParagraph } from "./typography/ClippedParagraph";

const CurrentVideo = ({ video, borderRadius = 0, style, hasLeftBorder = false }) => {
  const [modalOpen, setOpenModal] = useState(false);

  const toggleModal = () => {
    setOpenModal(!modalOpen);
  };

  // VideoPlayer expects the youtube video id as just `id`
  const videoWithId = { ...video, id: video.youtube_id };

  const date = formateDate(videoWithId.display_date);

  return (
    <>
      <Box component="article" style={{ overflow: "hidden", ...style }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={0}
          sx={{
            alignItems: "center",
            color: "common.white",
            "& a:hover": {
              textDecoration: "none",
            },
          }}
        >
          <Box
            sx={{
              flex: { xs: "1 1 100%", md: "1 1 58.33%" },
            }}
          >
            <Box
              sx={(theme) => ({
                px: 5,
                py: 4,
                position: "relative",
                "&::after": {
                  content: '" "',
                  display: "block",
                  width: "125%",
                  position: "absolute",
                  borderLeft: hasLeftBorder
                    ? `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`
                    : "none",
                  background: theme.palette.secondary.main,
                  top: 0,
                  left: 0,
                  right: "-100%",
                  bottom: 0,
                  zIndex: 1,
                  borderRadius,
                },
              })}
            >
              <Box sx={{ position: "relative", zIndex: 10, color: "text.dark" }}>
                <Box sx={{ alignItems: "center", display: "flex", gap: "10px", mb: 1 }}>
                  {date && <Typography variant="overline">{date}</Typography>}
                  <ItemBadges item={videoWithId} positioned={false} size="small" />
                </Box>
                <Typography component="h2" variant="h4" style={{ marginBottom: 10 }}>
                  {videoWithId.title}
                </Typography>
                {videoWithId.description && (
                  <ClippedParagraph text={videoWithId.description} textColor="light" />
                )}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              flex: { xs: "1 1 100%", md: "1 1 41.67%" },
              display: "flex",
              position: "relative",
              zIndex: 10,
            }}
          >
            <Box sx={{ display: "flex", flex: 1, mr: 4 }}>
              <VideoCard
                video={videoWithId}
                showDetails={false}
                handleClick={toggleModal}
                isFeatured={true}
                showChips={false}
              />
            </Box>
          </Box>
        </Stack>
      </Box>
      <VideoDialog modalOpen={modalOpen} toggleModal={toggleModal} video={videoWithId} />
    </>
  );
};

CurrentVideo.propTypes = {
  video: shape({
    youtube_id: string,
    display_date: string,
    title: string,
    description: string,
  }).isRequired,
  borderRadius: oneOfType([number, string]),
  style: object,
  hasLeftBorder: bool,
};

export default CurrentVideo;
